import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  CircularProgress,
  Collapse,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { VAT_RATES, DEFAULT_VAT_RATE, calcPriceInclVat, calcPriceExclVat, formatPrice, formatPriceWithCurrency, roundMoney } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import DecimalField from '../shared/DecimalField';
import PartAutocomplete from '../shared/PartAutocomplete';
import { useServerSearch } from '../../hooks/useServerSearch';
import { useUserStore } from '../../store';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

const SAVE_AS_OPTIONS = {
  NONE: 'none',
  PART: 'part',
  JOB: 'job',
};

const PRICE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'hourly', label: 'Hourly rate' },
];

// Common preset line items for workshops
const PRESET_LINES = [
  { key: 'pientarvikelisa', description: 'Pientarvikelisä', price: 5, unit: 'kpl' },
  { key: 'laskutuslisa', description: 'Laskutuslisä', price: 10, unit: 'kpl' },
  { key: 'kuljetus', description: 'Kuljetuskustannukset', price: 0, unit: 'kpl' },
];

const LINE_TYPES = {
  CUSTOM: 'custom',
  PART: 'part',
  JOB: 'job',
};

function AddInvoiceLineDialog({ open, handleClose, invoiceId, isCreditNote = false, onUpdate, defaultDiscountPercent }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled ?? false;
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const partsTabFirst = currentOrganization?.invoice_parts_tab_first ?? false;
  const [lineType, setLineType] = useState(partsTabFirst ? LINE_TYPES.PART : LINE_TYPES.CUSTOM);
  const [description, setDescription] = useState('');
  const [rowFreeText, setRowFreeText] = useState('');
  const [productCode, setProductCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('kpl');
  const [unitPrice, setUnitPrice] = useState('');
  const [vatRate, setVatRate] = useState(DEFAULT_VAT_RATE);
  const [discountPercent, setDiscountPercent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceIncludesVat, setPriceIncludesVat] = useState(orgDefaultVat);
  const [reduceFromInventory, setReduceFromInventory] = useState(true);
  const [stockInfo, setStockInfo] = useState(null);

  // Part picking is handled by the shared PartAutocomplete (registry +
  // vendor-catalogue search); it remounts fresh each time the dialog opens.
  const [selectedPart, setSelectedPart] = useState(null);

  // Jobs state - using server-side search hook
  const {
    items: jobs,
    loading: loadingJobs,
    autocompleteProps: jobsAutocompleteProps,
    reset: resetJobsSearch,
  } = useServerSearch('/api/jobs/', { loadOnMount: false });
  const [selectedJob, setSelectedJob] = useState(null);

  // Save to catalog state (for CUSTOM line type)
  const [saveAs, setSaveAs] = useState(SAVE_AS_OPTIONS.NONE);
  // Part-specific fields
  const [manufacturerCode, setManufacturerCode] = useState('');
  const [manufacturerName, setManufacturerName] = useState('');
  // Job-specific fields
  const [jobCode, setJobCode] = useState('');
  const [priceType, setPriceType] = useState('fixed');
  const [jobCategory, setJobCategory] = useState(null);
  const [jobCategories, setJobCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Load job categories
  const loadJobCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get('/api/job-categories/');
      setJobCategories(response.data);
    } catch (err) {
      console.error('Error loading job categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  // Sync customer default discount when dialog opens
  useEffect(() => {
    if (open) {
      setDiscountPercent(defaultDiscountPercent || '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when dialog opens and type changes
  useEffect(() => {
    if (open) {
      if (lineType === LINE_TYPES.JOB) {
        resetJobsSearch();
      }
      // Load job categories once for the save as job option
      if (jobCategories.length === 0) {
        loadJobCategories();
      }
    }
  }, [open, lineType, resetJobsSearch, loadJobCategories, jobCategories.length]);

  // Handle part selection - prefill form and fetch stock info
  const handlePartSelect = (part, meta = {}) => { // eslint-disable-line no-unused-vars
    setSelectedPart(part);
    setStockInfo(null);
    if (part) {
      setDescription(part.name || '');
      setProductCode(part.manufacturer_code || '');
      const pos = part.pos_prices;
      if (part.is_margin_vat_scheme) {
        // Margin-scheme prices are always gross
        setPriceIncludesVat(true);
        const marginGross = pos && parseFloat(pos.unit_price) > 0 ? pos.unit_price : part.sell_price || part.sell_price_incl_vat;
        setUnitPrice(marginGross || '');
      } else if (pos) {
        // Server-resolved walk-in price (sell_price, or cost x margin) — the
        // same figure the POS and work-order flows charge. A sell price
        // entered VAT-inclusive keeps the exact stored gross (no re-rounding).
        if (parseFloat(pos.unit_price) > 0) {
          if (pos.price_entered_with_vat) {
            setPriceIncludesVat(true);
            setUnitPrice(pos.unit_price_incl_vat);
          } else {
            setPriceIncludesVat(false);
            setUnitPrice(pos.unit_price);
          }
        } else {
          // Part has no prices at all
          setPriceIncludesVat(orgDefaultVat);
          setUnitPrice('');
        }
      } else if (part.sell_price) {
        // Part has explicit sell price - use it instead of purchase price
        if (part.sell_price_entered_with_vat && part.sell_price_incl_vat) {
          setPriceIncludesVat(true);
          setUnitPrice(part.sell_price_incl_vat);
        } else {
          setPriceIncludesVat(false);
          setUnitPrice(part.sell_price);
        }
      } else if (part.price_entered_with_vat && part.price_incl_vat) {
        setPriceIncludesVat(true);
        setUnitPrice(part.price_incl_vat);
      } else {
        setPriceIncludesVat(false);
        setUnitPrice(part.price || '');
      }
      setVatRate(parseFloat(part.vat_rate) || DEFAULT_VAT_RATE);
      setUnit(part.unit || 'kpl');
      setQuantity(1);
      setReduceFromInventory(true);
      // Fetch stock info if inventory is enabled
      if (inventoryEnabled && part.stock_info) {
        setStockInfo(part.stock_info);
      } else if (inventoryEnabled) {
        // Try fetching from the part detail endpoint
        axios.get(`/api/parts/${part.id}/`).then(res => {
          if (res.data?.stock_info) {
            setStockInfo(res.data.stock_info);
          }
        }).catch(() => {});
      }
    } else {
      setReduceFromInventory(true);
    }
  };

  // Handle job selection - prefill form
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    if (job) {
      setDescription(job.name || '');
      setProductCode(job.job_code || '');
      if (job.price_entered_with_vat && job.price_incl_vat) {
        setPriceIncludesVat(true);
        setUnitPrice(job.price_incl_vat);
      } else {
        setPriceIncludesVat(false);
        setUnitPrice(job.price || '');
      }
      setVatRate(parseFloat(job.vat_rate) || DEFAULT_VAT_RATE);
      setUnit(job.price_type === 'hourly' ? 'h' : 'kpl');
      setQuantity(1);
    }
  };

  const resetForm = () => {
    setLineType(partsTabFirst ? LINE_TYPES.PART : LINE_TYPES.CUSTOM);
    setDescription('');
    setRowFreeText('');
    setProductCode('');
    setQuantity(1);
    setUnit('kpl');
    setUnitPrice('');
    setVatRate(DEFAULT_VAT_RATE);
    setDiscountPercent(defaultDiscountPercent || '');
    setSelectedPart(null);
    setSelectedJob(null);
    resetJobsSearch();
    setPriceIncludesVat(orgDefaultVat);
    setReduceFromInventory(true);
    setStockInfo(null);
    setError(null);
    // Reset save to catalog fields
    setSaveAs(SAVE_AS_OPTIONS.NONE);
    setManufacturerCode('');
    setManufacturerName('');
    setJobCode('');
    setPriceType('fixed');
    setJobCategory(null);
  };

  const handlePresetClick = (preset) => {
    setDescription(preset.description);
    setUnitPrice(preset.price);
    setUnit(preset.unit);
    setQuantity(1);
  };

  const handleLineTypeChange = (event, newType) => {
    if (newType !== null) {
      setLineType(newType);
      // Reset form fields when switching type
      setDescription('');
      setRowFreeText('');
      setProductCode('');
      setQuantity(1);
      setUnit('kpl');
      setUnitPrice('');
      setVatRate(DEFAULT_VAT_RATE);
      setDiscountPercent('');
      setSelectedPart(null);
      setSelectedJob(null);
      setPriceIncludesVat(orgDefaultVat);
      setReduceFromInventory(true);
      setStockInfo(null);
      // Reset save to catalog fields
      setSaveAs(SAVE_AS_OPTIONS.NONE);
      setManufacturerCode('');
      setManufacturerName('');
      setJobCode('');
      setPriceType('fixed');
      setJobCategory(null);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get the entered price value
      const enteredPriceValue = parseFloat(unitPrice) || 0;
      const isMarginVat = lineType === LINE_TYPES.PART && selectedPart?.is_margin_vat_scheme;

      // Mirror backend sign rules so the user learns before the request.
      // Credit notes are exempt from the double-negative rule: crediting a
      // negative-price row legitimately produces a qty<0 x price<0 row.
      if (isMarginVat && enteredPriceValue < 0) {
        setError(t('invoices.marginNegativePriceError'));
        setLoading(false);
        return;
      }
      if (!isCreditNote && (parseFloat(quantity) || 0) < 0 && enteredPriceValue < 0) {
        setError(t('invoices.doubleNegativeError'));
        setLoading(false);
        return;
      }

      // Calculate VAT-exclusive price for storage
      let priceExclVat = enteredPriceValue;
      let priceInclVat = null;
      const enteredWithVat = (priceIncludesVat || isMarginVat) && enteredPriceValue !== 0;

      if (enteredWithVat) {
        // User entered VAT-inclusive price - store it as source of truth
        priceInclVat = enteredPriceValue;
        priceExclVat = calcPriceExclVat(enteredPriceValue, vatRate);
      }

      // If in CUSTOM mode and saveAs is set, create the part/job first
      if (lineType === LINE_TYPES.CUSTOM && saveAs === SAVE_AS_OPTIONS.PART) {
        // Create part (catalog items store VAT-exclusive prices)
        const partData = {
          name: description,
          price: priceExclVat || null,
          vat_rate: vatRate,
          manufacturer_code: manufacturerCode || null,
          manufacturer_name: manufacturerName || null,
          source_type: 'manual',
        };
        await axios.post('/api/parts/', partData);
      } else if (lineType === LINE_TYPES.CUSTOM && saveAs === SAVE_AS_OPTIONS.JOB) {
        // Create job (catalog items store VAT-exclusive prices)
        const jobData = {
          name: description,
          price: priceExclVat || 0,
          vat_rate: vatRate,
          job_code: jobCode || null,
          price_type: priceType,
          category: jobCategory?.id || null,
        };
        await axios.post('/api/jobs/', jobData);
      }

      // Create invoice line (always)
      const data = {
        line_type: lineType === LINE_TYPES.PART && selectedPart ? 'part'
                 : lineType === LINE_TYPES.JOB && selectedJob ? 'job'
                 : 'custom',
        description,
        row_free_text: rowFreeText,
        product_code: productCode,
        quantity: parseFloat(quantity) || 1,
        unit,
        unit_price_excl_vat: priceExclVat,
        unit_price_incl_vat: enteredWithVat ? priceInclVat : null,
        price_entered_with_vat: enteredWithVat,
        vat_rate: vatRate,
        discount_percent: parseFloat(discountPercent) || 0,
      };

      // Add part reference and inventory flag when a part is selected
      if (lineType === LINE_TYPES.PART && selectedPart) {
        data.part = selectedPart.id;
        // Only set reduce_from_inventory when the part has inventory tracking
        if (stockInfo) {
          data.reduce_from_inventory = reduceFromInventory;
        }
        // Margin VAT scheme: force gross price, include margin fields
        if (isMarginVat) {
          data.is_margin_vat_scheme = true;
          data.margin_vat_purchase_price = selectedPart.margin_vat_purchase_price;
          data.unit_price_incl_vat = enteredPriceValue;
          data.price_entered_with_vat = true;
        }
      }
      await axios.post(`/api/invoices/${invoiceId}/lines/`, data);
      // Fetch updated invoice
      const invoiceResponse = await axios.get(`/api/invoices/${invoiceId}/`);
      onUpdate(invoiceResponse.data);
      resetForm();
      handleClose();
    } catch (err) {
      console.error('Error adding line:', err);
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        // Collect field-level error messages
        const messages = Object.values(detail).flat().join(', ');
        setError(messages || t('Failed to add line'));
      } else {
        setError(err.response?.data?.detail || t('Failed to add line'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    handleClose();
  };

  // Calculations - account for VAT toggle and discount
  const enteredPrice = parseFloat(unitPrice || 0);
  const discountMultiplier = 1 - (parseFloat(discountPercent) || 0) / 100;
  // If price includes VAT, convert to excl VAT for calculations; otherwise use as-is
  const effectivePriceExclVat = priceIncludesVat ? calcPriceExclVat(enteredPrice, vatRate) : enteredPrice;
  const effectivePriceInclVat = priceIncludesVat ? enteredPrice : calcPriceInclVat(enteredPrice, vatRate);
  // Apply discount to line totals (roundMoney avoids floating-point display errors)
  const lineTotalExclVat = roundMoney(quantity * effectivePriceExclVat * discountMultiplier);
  const lineTotalInclVat = roundMoney(quantity * effectivePriceInclVat * discountMultiplier);
  const vatAmount = roundMoney(lineTotalInclVat - lineTotalExclVat);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {t('Add line')}
        </Typography>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Line type selector */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1.5 }}>
            {t('Line type')}
          </Typography>
          <ToggleButtonGroup
            value={lineType}
            exclusive
            onChange={handleLineTypeChange}
            size="small"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                flex: 1,
                px: { xs: 1, sm: 2 },
                py: 1,
                textTransform: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 0,
                '&:first-of-type': {
                  borderRadius: '8px 0 0 8px',
                },
                '&:last-of-type': {
                  borderRadius: '0 8px 8px 0',
                },
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              },
            }}
          >
            {partsTabFirst ? (
              <>
                <ToggleButton value={LINE_TYPES.PART}>
                  <SettingsIcon sx={{ mr: 1, fontSize: '18px' }} />
                  {t('Part')}
                </ToggleButton>
                <ToggleButton value={LINE_TYPES.JOB}>
                  <BuildIcon sx={{ mr: 1, fontSize: '18px' }} />
                  {t('Job')}
                </ToggleButton>
                <ToggleButton value={LINE_TYPES.CUSTOM}>
                  <EditIcon sx={{ mr: 1, fontSize: '18px' }} />
                  {t('Custom')}
                </ToggleButton>
              </>
            ) : (
              <>
                <ToggleButton value={LINE_TYPES.CUSTOM}>
                  <EditIcon sx={{ mr: 1, fontSize: '18px' }} />
                  {t('Custom')}
                </ToggleButton>
                <ToggleButton value={LINE_TYPES.PART}>
                  <SettingsIcon sx={{ mr: 1, fontSize: '18px' }} />
                  {t('Part')}
                </ToggleButton>
                <ToggleButton value={LINE_TYPES.JOB}>
                  <BuildIcon sx={{ mr: 1, fontSize: '18px' }} />
                  {t('Job')}
                </ToggleButton>
              </>
            )}
          </ToggleButtonGroup>
        </Box>

        {/* Part selection */}
        {lineType === LINE_TYPES.PART && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Select part')}
            </Typography>
            <PartAutocomplete
              onSelect={handlePartSelect}
              placeholder={t('Search parts...')}
              sourceToggle
              catalogueFilters
              stockMatchCheck
              loadOnMount
              clearOnSelect={false}
              shelfOnlyDefault={false}
              persistKey={null}
              inputTestId="invoice-part-search-input"
            />

            {/* Inventory info when part is selected */}
            {inventoryEnabled && selectedPart && stockInfo && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('Free')}: {formatQuantity(stockInfo.quantity_free)} {t('pcs')}
                  </Typography>
                  {parseFloat(quantity) > parseFloat(stockInfo.quantity_free) && (
                    <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                      ({t('Stock warning: quantity exceeds free stock')})
                    </Typography>
                  )}
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reduceFromInventory}
                      onChange={(e) => setReduceFromInventory(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('Reduce from inventory')}
                    </Typography>
                  }
                  sx={{ ml: 0 }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Job selection */}
        {lineType === LINE_TYPES.JOB && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Select job')}
            </Typography>
            <Autocomplete
              options={jobs}
              getOptionLabel={(option) => {
                const code = option.job_code ? ` (${option.job_code})` : '';
                const jobDisplayPrice = orgDefaultVat ? (option.price_incl_vat ?? option.price) : option.price;
                const price = jobDisplayPrice ? ` - ${formatPriceWithCurrency(jobDisplayPrice)}` : '';
                return `${option.name}${code}${price}`;
              }}
              value={selectedJob}
              onChange={(event, newValue) => handleJobSelect(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              {...jobsAutocompleteProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                  placeholder={t('Search jobs...')}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingJobs ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const jobPrice = orgDefaultVat ? (option.price_incl_vat ?? option.price) : option.price;
                return (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.job_code && `${option.job_code} • `}
                      {option.category_name && `${option.category_name} • `}
                      {formatPriceWithCurrency(jobPrice)} ({orgDefaultVat ? t('incl. VAT') : t('excl. VAT')})
                    </Typography>
                  </Box>
                </li>
                );
              }}
              noOptionsText={t('No jobs found')}
            />
          </Box>
        )}

        {/* Quick presets - only for custom type */}
        {lineType === LINE_TYPES.CUSTOM && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1.5 }}>
              {t('Quick add')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {PRESET_LINES.map((preset) => (
                <Chip
                  key={preset.key}
                  label={t(preset.description)}
                  onClick={() => handlePresetClick(preset)}
                  icon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Save to catalog - only for custom type */}
        {lineType === LINE_TYPES.CUSTOM && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SaveIcon sx={{ fontSize: '18px', color: 'text.secondary' }} />
              {t('Save to catalog')}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={saveAs}
                onChange={(e) => setSaveAs(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value={SAVE_AS_OPTIONS.NONE}>{t('Do not save')}</MenuItem>
                <MenuItem value={SAVE_AS_OPTIONS.PART}>{t('Save as part')}</MenuItem>
                <MenuItem value={SAVE_AS_OPTIONS.JOB}>{t('Save as job')}</MenuItem>
              </Select>
            </FormControl>

            {/* Part-specific fields */}
            <Collapse in={saveAs === SAVE_AS_OPTIONS.PART}>
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: 1.5, color: 'text.secondary' }}>
                  {t('Part details')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label={t('Manufacturer code')}
                    value={manufacturerCode}
                    onChange={(e) => setManufacturerCode(e.target.value)}
                    size="small"
                    sx={{
                      flex: 1,
                      minWidth: '150px',
                      '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                    }}
                  />
                  <TextField
                    label={t('Manufacturer name')}
                    value={manufacturerName}
                    onChange={(e) => setManufacturerName(e.target.value)}
                    size="small"
                    sx={{
                      flex: 1,
                      minWidth: '150px',
                      '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                    }}
                  />
                </Box>
              </Paper>
            </Collapse>

            {/* Job-specific fields */}
            <Collapse in={saveAs === SAVE_AS_OPTIONS.JOB}>
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: 1.5, color: 'text.secondary' }}>
                  {t('Job details')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    label={t('Job code')}
                    value={jobCode}
                    onChange={(e) => setJobCode(e.target.value)}
                    placeholder="e.g., OIL-01"
                    size="small"
                    sx={{
                      flex: 1,
                      minWidth: '120px',
                      '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>{t('Price type')}</InputLabel>
                    <Select
                      value={priceType}
                      onChange={(e) => setPriceType(e.target.value)}
                      label={t('Price type')}
                      sx={{ borderRadius: '8px' }}
                    >
                      {PRICE_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Autocomplete
                  value={jobCategory}
                  onChange={(event, newValue) => setJobCategory(newValue)}
                  options={jobCategories}
                  getOptionLabel={(option) => option.name || ''}
                  loading={loadingCategories}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      label={t('Category')}
                      placeholder={t('Select category...')}
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </Paper>
            </Collapse>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Description')} *
          </Typography>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('Line description')}
            fullWidth
            size="small"
            autoFocus={lineType === LINE_TYPES.CUSTOM}
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Additional Description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Additional description')}
          </Typography>
          <TextField
            value={rowFreeText}
            onChange={(e) => setRowFreeText(e.target.value)}
            placeholder={t('Optional')}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Product Code */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Product code')}
          </Typography>
          <TextField
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            placeholder={t('Optional')}
            size="small"
            sx={{
              width: '200px',
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Quantity and Unit */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Quantity')}
            </Typography>
            <DecimalField
              value={quantity}
              onChange={setQuantity}
              allowNegative
              size="small"
              inputProps={{ 'data-testid': 'invoice-line-quantity' }}
              sx={{
                width: '120px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Unit')}
            </Typography>
            <TextField
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="kpl"
              size="small"
              sx={{
                width: '100px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
        </Box>

        {/* Price and VAT Rate */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {selectedPart?.is_margin_vat_scheme
                ? t('Customer price')
                : (priceIncludesVat ? t('Unit price (incl. VAT)') : t('Unit price (excl. VAT)'))}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1 }}>
              <DecimalField
                value={unitPrice}
                onChange={setUnitPrice}
                allowNegative={!selectedPart?.is_margin_vat_scheme}
                size="small"
                inputProps={{ 'data-testid': 'invoice-line-unit-price' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{
                  width: { xs: '100%', sm: '150px' },
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
              {!selectedPart?.is_margin_vat_scheme && (
                <VatModeToggle
                  value={priceIncludesVat ? 'incl' : 'excl'}
                  onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                  sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                />
              )}
            </Box>
            {/* Show calculated price - hidden for margin VAT parts */}
            {!selectedPart?.is_margin_vat_scheme && (
              <VatConverseLine value={unitPrice} mode={priceIncludesVat ? 'incl' : 'excl'} vatRate={vatRate} />
            )}
            {parseFloat(unitPrice) < 0 && (
              <Typography sx={{ fontSize: '12px', color: 'warning.main' }}>
                {t('negativePriceHint')}
              </Typography>
            )}
          </Box>
          {!selectedPart?.is_margin_vat_scheme && (
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                {t('VAT rate')}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
                <Select
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  {VAT_RATES.map((rate) => (
                    <MenuItem key={rate.value} value={rate.value}>
                      {rate.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Discount %')}
            </Typography>
            <DecimalField
              value={discountPercent}
              onChange={setDiscountPercent}
              max={100}
              size="small"
              placeholder="0"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{
                width: '100px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
        </Box>

        {unitPrice && !!parseFloat(unitPrice) && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Line Total with VAT breakdown */}
            {selectedPart?.is_margin_vat_scheme ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {t('Total')}
                  </Typography>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                    {formatPrice(lineTotalInclVat)} €
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                  {t('VAT is calculated on profit margin (margin VAT scheme)')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {t('Subtotal (excl. VAT)')}
                  </Typography>
                  <Typography sx={{ fontSize: '14px' }}>
                    {formatPrice(lineTotalExclVat)} €
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {t('VAT')} ({formatPrice(vatRate, 1)} %)
                  </Typography>
                  <Typography sx={{ fontSize: '14px' }}>
                    {formatPrice(vatAmount)} €
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {t('Total (incl. VAT)')}
                  </Typography>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                    {formatPrice(lineTotalInclVat)} €
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCancel}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !description.trim()}
          sx={{
            bgcolor: 'secondary.main',
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {t('Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddInvoiceLineDialog;
